import { useEffect, useMemo, useRef } from 'react'
import Uppy from '@uppy/core'
import type { UppyFile } from '@uppy/core'
import GoldenRetriever from '@uppy/golden-retriever'
import zhCN from '@uppy/locales/lib/zh_CN'
import Tus from '@uppy/tus'
import DashboardModal from '@uppy/react/dashboard-modal'
import '@uppy/core/css/style.min.css'
import '@uppy/dashboard/css/style.min.css'
import { authStorage } from '@/lib/auth-storage'

const TUS_ENDPOINT = '/api/v1/materials/uploads/'
const GOLDEN_RETRIEVER_EXPIRES = 24 * 60 * 60 * 1000

interface MaterialUploadMeta extends Record<string, unknown> {
	folder_id: string
	filename?: string
	filetype?: string
}

function removeTusFingerprint(file?: UppyFile<MaterialUploadMeta, Record<string, never>>) {
	if (!file) return
	const fingerprint = ['tus', file.id, TUS_ENDPOINT].join('-')
	const prefix = `tus::${fingerprint}::`
	for (let i = localStorage.length - 1; i >= 0; i--) {
		const key = localStorage.key(i)
		if (key?.startsWith(prefix)) {
			localStorage.removeItem(key)
		}
	}
}

interface MaterialUploadDashboardProps {
	open: boolean
	folderId: number
	folderName: string
	isDark?: boolean
	onOpenChange: (open: boolean) => void
	onComplete: () => void
}

export function MaterialUploadDashboard({
	open,
	folderId,
	folderName,
	isDark = false,
	onOpenChange,
	onComplete,
}: MaterialUploadDashboardProps) {
	const onCompleteRef = useRef(onComplete)
	const onOpenChangeRef = useRef(onOpenChange)
	const destroyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		onCompleteRef.current = onComplete
	}, [onComplete])

	useEffect(() => {
		onOpenChangeRef.current = onOpenChange
	}, [onOpenChange])

	const uppy = useMemo(() => {
		const instance = new Uppy<MaterialUploadMeta>({
			autoProceed: false,
			locale: zhCN,
			meta: {
				folder_id: String(folderId),
			},
		}).use(Tus, {
			endpoint: TUS_ENDPOINT,
			headers: () => {
				const token = authStorage.getToken()
				const headers: Record<string, string> = {}
				if (token) headers.Authorization = `Bearer ${token}`
				return headers
			},
			allowedMetaFields: ['folder_id', 'filename', 'filetype'],
			retryDelays: [0, 1000, 3000, 5000],
			removeFingerprintOnSuccess: true,
		}).use(GoldenRetriever, { expires: GOLDEN_RETRIEVER_EXPIRES })
		instance.on('file-added', (file) => {
			instance.setFileMeta(file.id, {
				folder_id: String(folderId),
				filename: file.name,
				filetype: file.type || 'application/octet-stream',
			})
		})
		instance.on('file-removed', removeTusFingerprint)
		instance.on('upload-error', removeTusFingerprint)
		instance.on('complete', () => onCompleteRef.current())
		return instance
	}, [folderId])

	useEffect(() => {
		uppy.setMeta({ folder_id: String(folderId) })
	}, [uppy, folderId])

	useEffect(() => {
		if (destroyTimerRef.current) {
			clearTimeout(destroyTimerRef.current)
			destroyTimerRef.current = null
		}

		return () => {
			destroyTimerRef.current = setTimeout(() => {
				uppy.destroy()
				destroyTimerRef.current = null
			}, 0)
		}
	}, [uppy])

	return (
		<DashboardModal
			uppy={uppy}
			open={open}
			onRequestClose={() => onOpenChange(false)}
			doneButtonHandler={() => onOpenChangeRef.current(false)}
			proudlyDisplayPoweredByUppy={false}
			theme={isDark ? 'dark' : 'light'}
			note={`上传到：${folderName}`}
		/>
	)
}
